export type TestimonialCategory = "customer" | "producer" | "driver";

export interface TestimonialMedia {
  captionsUrl: string;
  transcriptUrl: string;
  posterAlt: string;
}

export interface Testimonial {
  id: string;
  category: TestimonialCategory;
  name: string;
  initials: string;
  city: string;
  theme: string[];
  role?: string;
  stat?: string;
  quote: string;
  /** Left null on every seed entry — no real video testimonials exist yet. */
  media: TestimonialMedia | null;
  /** Optional cross-link, e.g. a producer story referencing a fair-pricing initiative. */
  linkTo?: { label: string; href: string };
}

// Composite-persona convention (first name + last initial, no surname, no
// independently-verifiable business name) — the same one already
// established in About.tsx's FISHERMEN and Home.tsx's TESTIMONIALS arrays.
// "Priya R." and "Arjun Nair" below are the same personas already used on
// Home.tsx, kept consistent rather than reinvented. No photos: an
// initials-avatar badge stands in (TestimonialCard.tsx), matching
// FISHERMEN's existing treatment — a photo of a specific person is a
// stronger "this is a real individual" claim than a name+quote, so none is
// fabricated (see ZP-10's founder-photo precedent). Every `media` field is
// null: the video-testimonial mechanism (TestimonialVideoLightbox.tsx) is
// fully built and ready, but no real video testimonial exists yet.
// Consent capture (ZP-37/38's AC) is N/A for these entries specifically —
// there's no real, identifiable individual to have consented. Real consent
// belongs to ZP-40 (a genuine future submitter) and ZP-41 (moderation).
export const TESTIMONIALS: Testimonial[] = [
  {
    id: "ramesh-p",
    category: "producer",
    name: "Ramesh P.",
    initials: "RP",
    city: "Konkan Coast",
    theme: ["Fair Pricing"],
    stat: "+40% household income",
    quote:
      "Selling straight through ZenFresh means I know the price before I even leave the dock. No more haggling with three middlemen before the fish reaches anyone's table.",
    media: null,
    linkTo: { label: "See our sustainable sourcing commitments", href: "/compliance" },
  },
  {
    id: "sunitha-k",
    category: "producer",
    name: "Sunitha K.",
    initials: "SK",
    city: "Malabar Coast",
    theme: ["Community Impact"],
    stat: "3x order volume in 18 months",
    quote:
      "My daughters help me photograph the morning catch for the app now. It's strange to think people in Bangalore are eating fish I pulled in a few hours earlier.",
    media: null,
  },
  {
    id: "arjun-nair",
    category: "customer",
    name: "Arjun Nair",
    initials: "AN",
    city: "Kochi",
    theme: ["Consistency", "Freshness"],
    role: "Executive Chef",
    quote:
      "I switched my restaurant's entire seafood supply to ZenFresh eighteen months ago. Consistency is the real win — every delivery matches what was promised, every time.",
    media: null,
  },
  {
    id: "priya-r",
    category: "customer",
    name: "Priya R.",
    initials: "PR",
    city: "Mumbai",
    theme: ["Delivery Speed", "Convenience"],
    role: "Home Cook",
    quote:
      "I used to only cook fish on weekends because the market trip was such a production. Now it's a Tuesday-night thing. That's the actual change in my life.",
    media: null,
  },
  {
    id: "suresh-m",
    category: "driver",
    name: "Suresh M.",
    initials: "SM",
    city: "Chennai",
    theme: ["Earnings", "Flexibility"],
    stat: "Gold Tier · 500+ Deliveries",
    quote:
      "I pick my own hours around my kids' school schedule now. The per-delivery rate is shown before I accept, so there's no guessing what a shift is actually worth.",
    media: null,
  },
  {
    id: "divya-k",
    category: "driver",
    name: "Divya K.",
    initials: "DK",
    city: "Kozhikode",
    theme: ["Earnings", "Growth"],
    stat: "Silver Tier · 250+ Deliveries",
    quote:
      "Started part-time on weekends. The streak bonuses added up faster than I expected, and now it's steady enough that I've made it my main income.",
    media: null,
  },
];
